<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { switchFrame } from '@@/bits/whiteboard'
import { isEditorIdle } from '@@/bits/whiteboard_preview_utils'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import OzBasePopover, { OzBasePopoverColors } from '@@/library/v4/components/OzBasePopover.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFramePreviewStore } from '@@/pinia/whiteboard_frame_preview'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import WhiteboardFramePreviewOld from '@@/vuecomponents/WhiteboardFramePreviewOld.vue'
import { autoPlacement, offset } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import { storeToRefs } from 'pinia'
import type { TLPageId } from 'tldraw'
import { react } from 'tldraw'
import { computed, nextTick, onMounted, ref } from 'vue'

const surfaceStore = useSurfaceStore()
const whiteboardStore = useWhiteboardStore()
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()

const { isContainerSmallerThanTabletPortrait } = storeToRefs(surfaceContainerSizeStore)
const { isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)
const props = defineProps<{
  maxHeight: number
  xFrameContextMenu: boolean
}>()

const emit = defineEmits<{
  (e: 'show-frame-context-menu', anchor: Anchor, frameId: string): void
}>()

/**
 * TOOLTIP
 */
const xTooltip = ref(false)
const buttonRef = ref<typeof OzBaseButton | null>(null)
const buttonEl = computed(() => buttonRef.value?.$el)
const tooltipTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
function handleButtonHover(): void {
  // Cancellable delayed tooltip
  tooltipTimeout.value = setTimeout(() => {
    xTooltip.value = true
  }, 500)
}

function handleButtonLeave(): void {
  // Clear tooltip timeout
  if (tooltipTimeout.value) {
    clearTimeout(tooltipTimeout.value)
    tooltipTimeout.value = null
  }
  xTooltip.value = false
}

function handleSwitchFrame(frameId: TLPageId): void {
  trackEvent('Whiteboard', 'Switched current frame', null, null, { context: 'Frame bar' })
  switchFrame(frameId)
}

/**
 * VISUALS
 */

// COLOR SCHEME
// Currently follow background luminance of surface (based on walllpaper)
const colorScheme = computed((): 'dark' | 'light' => {
  return surfaceStore.backgroundLuminance
})

const helperText = computed(() => {
  if (whiteboardStore.hasReachedPlanFrameLimitAndCannotUpgrade) {
    return __('At %{whiteboardFrameLimit} card limit for this sandbox', {
      whiteboardFrameLimit: surfaceStore.whiteboardFrameLimit,
    })
  }
  return whiteboardStore.isSingleFrame ? __('Add a new card') : __('Add a card below the current card')
})
const containerRef = ref<HTMLElement | null>(null)
const MIN_HEIGHT = 100
const height = ref<number>(MIN_HEIGHT)

// Animate height when new frame is added
onMounted(() => {
  if (isContainerSmallerThanTabletPortrait.value) {
    return
  }
  nextTick(() => {
    height.value = Math.max(containerRef.value?.scrollHeight ?? 0, MIN_HEIGHT)
    if (!containerRef.value) return
    const observer = new MutationObserver(() => {
      if (containerRef.value) {
        height.value = containerRef.value.scrollHeight
      }
    })
    observer.observe(containerRef.value, { childList: true, subtree: true })
  })
})

/** FRAME LOGIC */
const { frames, currentFrameId } = storeToRefs(whiteboardStore)

function addFrame(): void {
  trackEvent('Whiteboard', 'Pressed add frame button', null, null, { context: 'Frame bar' })
  whiteboardStore.addFrameBelow(whiteboardStore.currentFrameId!)
}

/** FRAME PREVIEWS */
const whiteboardFramePreviewStore = useWhiteboardFramePreviewStore()
const { framePreviews } = storeToRefs(whiteboardFramePreviewStore)
// Mount life-cycle logic:
// Update initial frame previews
// Listen to document changes and trigger preview update
onMounted(() => {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }

  whiteboardFramePreviewStore.updatePreviewForAllFrames()

  // mark frame as having changes for preview reloading
  editor.store.listen(whiteboardFramePreviewStore.debouncedHandlePageChange, { scope: 'document', source: 'all' })

  // Update frames with changes when editor.getPath() changes
  react('update pending frame previews', () => {
    if (!isEditorIdle()) {
      return
    }
    if (whiteboardFramePreviewStore.framesWithPreviewChanges.length === 0) {
      return
    }
    whiteboardFramePreviewStore.conditionallyUpdateFramesWithChanges()
  })
})

/**
 * FRAME CONTEXT MENU
 */

function showContextMenu(e: PointerEvent | MouseEvent, frameId: string) {
  const buttonAnchor = e.currentTarget as Element
  const rect = buttonAnchor.getBoundingClientRect()
  emit('show-frame-context-menu', { x: rect.right, y: rect.top }, frameId)
}

/** PERMISSION */
const whiteboardPermissionsStore = useWhiteboardPermissionsStore()

const surfaceWindowMobileNotchStore = useSurfaceWindowMobileNotchStore()
</script>

<template>
  <div
    v-if="frames.length > 0"
    :style="{
      insetInlineStart: !isLoadingPortraitMobileLayout
        ? surfaceWindowMobileNotchStore.safeAreaInsetStart + 'px'
        : '50%',
    }"
    :class="[
      // Position
      'absolute',
      'z-edit-post',

      !isLoadingPortraitMobileLayout && [
        // Desktop position
        'top-1/2 -translate-y-1/2',
        // Size
        'w-[100px]',
        // Shape
        'rounded-e-3xl',
        surfaceWindowMobileNotchStore.isApplyingSafeAreaInsetStart ? 'rounded-3xl' : 'rounded-s-none',
      ],
      isLoadingPortraitMobileLayout && [
        // Mobile position
        'top-0 start-1/2 -translate-s-1/2',
        // Shape
        'rounded-2xl',
        // Size
        'max-w-[calc(100%-1.5rem)]',
      ],

      // Color
      {
        'bg-popover-dark': colorScheme === 'dark',
        'bg-popover-light': colorScheme === 'light',
        'backdrop-blur-10': true,
      },

      // Children positioning
      'justify-center',
      'overflow-hidden',
    ]"
  >
    <div
      ref="containerRef"
      data-testid="whiteboardFrameBar"
      :class="[
        'relative',
        // Shape
        'max-h-full',
        !isLoadingPortraitMobileLayout && [
          // Children paddings
          'py-3 ps-3',
          'pe-4',
          // Size
          'box-border', // So that the calculation on the next line is correct
          // Children positioning
          'flex flex-col',
          // Gap
          'gap-y-3',
          'overflow-y-auto overflow-x-hidden',
        ],
        isLoadingPortraitMobileLayout && [
          // Children paddings
          'py-0 pe-0.5',
          {
            'ps-2': !surfaceStore.isFrozen,
          },

          // Size
          'box-border', // So that the calculation on the next line is correct
          'h-[100px]',
          'max-w-full',
          // Children positioning
          'flex flex-row',
          // Gap
          'gap-x-1',
        ],
        'transition-all',
        'items-start',
        {
          'scrollbar-photo-thin-dark-on-hover': colorScheme === 'dark' && height > props.maxHeight,
          'scrollbar-photo-thin-on-hover': colorScheme === 'light' && height > props.maxHeight,
          'scrollbar-thin-transparent': height <= props.maxHeight,
        },
        !device.safari && !device.firefox && 'scrollbar-gutter-stable',
      ]"
      :style="
        !isLoadingPortraitMobileLayout
          ? {
              maxHeight: props.maxHeight + 'px',
            }
          : {}
      "
    >
      <!-- Add frame button -->
      <div
        v-if="whiteboardPermissionsStore.canIManage && !surfaceStore.isFrozen"
        :class="{ 'h-full box-border py-2': isLoadingPortraitMobileLayout }"
      >
        <OzBaseButton
          ref="buttonRef"
          data-testid="addWhiteboardFrameButton"
          :aria-label="helperText"
          :class="[
            // Shape
            {
              'w-18 h-6': !isLoadingPortraitMobileLayout,
              'w-10 h-full': isLoadingPortraitMobileLayout,
            },
            'box-border',
            'rounded-lg',
            'shrink-0',
            // Don't use disabled property because we still want pointer events to trigger the tooltip
            whiteboardStore.hasReachedPlanFrameLimitAndCannotUpgrade && '!cursor-not-allowed',

            // Color
            colorScheme === 'dark'
              ? [
                  'bg-button-dark',
                  'hhover:bg-button-hover-dark',
                  'hactive:bg-canary-500',
                  'focus-visible:ring-2',
                  'focus-visible:ring-offset-1',
                  'focus-visible:ring-offset-popover-dark',
                  'focus-visible:ring-canary-500',
                ]
              : '',
            colorScheme === 'light'
              ? [
                  'bg-button-light',
                  'hhover:bg-button-hover-light',
                  'hactive:bg-grape-500',
                  'focus-visible:ring-2',
                  'focus-visible:ring-offset-1',
                  'focus-visible:ring-offset-popover-light',
                  'focus-visible:ring-grape-500',
                ]
              : '',

            // Transition
            'transition-all',
            'duration-150',
            'ease-in-out',

            // Children positioning
            'flex',
            'items-center',
            'justify-center',

            // Children styling
            'text-body-small',
            'font-semibold',
            {
              'text-light-text-100': colorScheme === 'dark',
              'text-dark-text-100': colorScheme === 'light',
            },
            {
              'hactive:text-dark-text-100': colorScheme === 'dark',
              'hactive:text-light-text-100': colorScheme === 'light',
            },
          ]"
          @pointerenter="handleButtonHover"
          @pointerleave="handleButtonLeave"
          @click="addFrame"
        >
          <OzIcon font="oricon" :name="'plus_thick'" :size="16"></OzIcon>
        </OzBaseButton>
      </div>
      <!-- Frames -->
      <div
        :class="
          !isLoadingPortraitMobileLayout
            ? ['contents']
            : [
                'flex',
                'overflow-x-auto overflow-y-hidden',
                {
                  'scrollbar-photo-thin-dark-on-hover': colorScheme === 'dark',
                  'scrollbar-photo-thin-on-hover': colorScheme === 'light',
                },
                'gap-x-1.5',
                'h-full pt-2 ps-3 pe-2 box-border',
              ]
        "
      >
        <WhiteboardFramePreviewOld
          v-for="(frame, index) in frames"
          ref="frameList"
          :key="frame.id"
          :frame-id="frame.id"
          :frame-index="index + 1"
          :current-frame-id="currentFrameId"
          :color-scheme="colorScheme"
          :is-loading-portrait-mobile-layout="isLoadingPortraitMobileLayout"
          :preview="framePreviews[frame.id]"
          :is-draggable="whiteboardPermissionsStore.canIEdit && !surfaceStore.isFrozen"
          @switch-frame="handleSwitchFrame"
          @show-context-menu="showContextMenu"
          @move-frame-above="whiteboardStore.moveFrameAbove"
          @move-frame-below="whiteboardStore.moveFrameBelow"
        ></WhiteboardFramePreviewOld>
      </div>
    </div>

    <!-- Use a portal so tooltip can escape container div which has overflow: hidden -->
    <Portal selector="#surface-container">
      <!-- Add new frame tooltip -->
      <Transition
        enter-active-class="transition-opacity duration-250 ease-out motion-reduce:transition-none"
        leave-active-class="transition-opacity duration-150 ease-in motion-reduce:transition-none"
        enter-class="opacity-0"
        leave-to-class="opacity-0"
        appear
      >
        <OzBasePopover
          v-if="xTooltip && buttonEl"
          :class="[
            'px-3 py-1',
            'pointer-events-none',
            {
              'bg-common-ui-d70 ': colorScheme === 'dark',
              'bg-common-ui-l86': colorScheme === 'light',
            },
            'backdrop-blur-6',
            'w-max',
          ]"
          :config="{
            middleware: [
              offset(16),
              autoPlacement({
                allowedPlacements: ['top', 'top-start', 'top-end'],
              }),
            ],
          }"
          role="tooltip"
          :anchor="buttonEl"
          :radius="8"
          :color="OzBasePopoverColors.None"
        >
          <span :class="colorScheme === 'dark' ? 'text-light-text-100' : 'text-dark-text-100'">{{ helperText }}</span>
        </OzBasePopover>
      </Transition>
    </Portal>
  </div>
</template>
